/**
 * ENT-6474: part of the Supabase client's `global.fetch`. An app sign-out revokes
 * its session on the server as soon as the logout request arrives, but the auth
 * library only removes the stored session once it has processed the response, and
 * its reads take no lock. A PostgREST request that starts in that window (a hook
 * mounting during app load, say) picks up the stored token and is rejected
 * `403 28000 session_revoked`, which reads as a defect in a correct sign-out. An
 * Edge Function call answers `401 User not authenticated` (ENT-6502), and may carry
 * a token its caller captured earlier rather than the stored one.
 *
 * So while a sign-out runs, a PostgREST or Edge Function request bearing the ending
 * session is held, then sent once with the same user's session if the client holds one when
 * the sign-out settles (a replacement signing in meanwhile). It never goes out under
 * another user's session: that user did not ask for it, and the rejection it would
 * have had becomes their read or write. Nor does it go out anonymously: a write could
 * run unattributed, and an empty answer would read as that user's real data.
 *
 * With no session of that user left, the page is usually about to navigate away, so
 * the request waits for that. A page that stays past the grace gets the request
 * back as failed, so nothing hangs, as does a caller that aborts while it waits.
 * Requests bearing another session are never held, and auth traffic, the logout
 * itself included, is never touched.
 *
 * A request already on the wire when the logout is sent cannot be recalled. While
 * the sign-out runs, `protectedFunctionAuth` does not recover its rejection with a
 * refresh, which would race the logout that ended the session.
 */
import { decodeJwt } from 'jose';

interface EndingSession {
  sessionId: string;
  settled: Promise<void>;
}

const endingSessions = new Set<EndingSession>();

const BEARER_PREFIX = 'Bearer ';

/** How long a held request waits for the page to leave once no session of its user is left. */
export const ENDED_SESSION_READ_GRACE_MS = 5_000;

/**
 * Hold this session's requests until the returned release is called,
 * which the sign-out must do once the session has left storage, on every outcome.
 * An unreadable session (null) cannot be matched, so nothing is held for it.
 */
export function holdRequestsOfEndingSession(sessionId: string | null): () => void {
  if (!sessionId) {
    return () => {};
  }
  let settle!: () => void;
  const ending: EndingSession = { sessionId, settled: new Promise((resolve) => { settle = resolve; }) };
  endingSessions.add(ending);
  return () => {
    endingSessions.delete(ending);
    settle();
  };
}

function claimsOf(token: string): { sessionId: unknown; userId: unknown } {
  try {
    const claims = decodeJwt(token);
    return { sessionId: claims.session_id, userId: claims.sub };
  } catch {
    // Not a JWT (a publishable key): it bears no session.
    return { sessionId: undefined, userId: undefined };
  }
}

function settlementsHolding(token: string): Promise<void>[] {
  const { sessionId } = claimsOf(token);
  return [...endingSessions].filter((ending) => ending.sessionId === sessionId).map((ending) => ending.settled);
}

/** Whether a sign-out is running for the session this token bears. */
export function isSessionEnding(accessToken: string): boolean {
  return settlementsHolding(accessToken).length > 0;
}

function isFencedRequest(fencedUrls: readonly string[], input: RequestInfo | URL): boolean {
  // supabase-js sends a URL string and an init, which is where the bearer is read from.
  if (typeof input !== 'string' && !(input instanceof URL)) {
    return false;
  }
  const url = String(input);
  return fencedUrls.some((fencedUrl) => url.startsWith(fencedUrl));
}

/**
 * A held request that cannot be sent fails as an AbortError because PostgREST retries
 * any other fetch failure of a read, and the auth library sends that retry with the
 * publishable key when it has no session. The read models retry any other failure of
 * an Edge Function call too.
 */
function abortHeldRequest(reason: string): DOMException {
  return new DOMException(reason, 'AbortError');
}

/** Rejects when the caller aborts: a waiting request is released at once, not at the settlement. */
function callerAbort(signal: AbortSignal): Promise<never> {
  return new Promise((_, reject) => {
    const abort = () => reject(abortHeldRequest('The caller aborted this request while it was held'));
    if (signal.aborted) {
      abort();
    } else {
      signal.addEventListener('abort', abort, { once: true });
    }
  });
}

interface EndingSessionFetchOptions {
  /** `<SUPABASE_URL>/rest/v1/` and `<SUPABASE_URL>/functions/v1/`: only requests under them are considered. */
  fencedUrls: readonly string[];
  /** The stored session's token, or null when there is none. Rejects when the session cannot be read. */
  currentAccessToken: () => Promise<string | null>;
  baseFetch: typeof fetch;
}

export function createEndingSessionFetch({ fencedUrls, currentAccessToken, baseFetch }: EndingSessionFetchOptions): typeof fetch {
  return async (input, init) => {
    if (endingSessions.size === 0 || !isFencedRequest(fencedUrls, input)) {
      return baseFetch(input, init);
    }
    const headers = new Headers(init?.headers);
    const authorization = headers.get('Authorization');
    if (!authorization?.startsWith(BEARER_PREFIX)) {
      return baseFetch(input, init);
    }

    const bearer = authorization.slice(BEARER_PREFIX.length);
    let holding = settlementsHolding(bearer);
    if (holding.length === 0) {
      return baseFetch(input, init);
    }
    const { userId } = claimsOf(bearer);
    const aborted = init?.signal ? callerAbort(init.signal) : null;
    const whileHeld = <T>(wait: Promise<T>) => (aborted ? Promise.race([wait, aborted]) : wait);
    const sameUserToken = async () => {
      let current: string | null;
      try {
        current = await currentAccessToken();
      } catch (error) {
        // An unreadable session is unknown, not absent.
        throw abortHeldRequest(`The session could not be read: ${error instanceof Error ? error.message : 'unknown error'}`);
      }
      return current !== null && userId !== undefined && claimsOf(current).userId === userId ? current : null;
    };
    let token = bearer;
    // Re-checked after each wait: the token may belong to a sign-out begun meanwhile.
    while (holding.length > 0) {
      await whileHeld(Promise.all(holding));
      let current = await sameUserToken();
      if (current === null) {
        await whileHeld(new Promise((resolve) => setTimeout(resolve, ENDED_SESSION_READ_GRACE_MS)));
        current = await sameUserToken();
      }
      if (current === null) {
        throw abortHeldRequest('The session ended before this request could be sent');
      }
      token = current;
      holding = settlementsHolding(token);
    }
    headers.set('Authorization', `${BEARER_PREFIX}${token}`);
    return baseFetch(input, { ...init, headers });
  };
}
